/**
 * The tools a jobsite offers a browsing agent.
 *
 * Every one makes the same call the site's own UI makes, so an agent-driven
 * lead lands in the same inbox under the same bot protection, rate limits and
 * quota. Nothing here is an agent-only back door.
 *
 * Neither write flow completes unaided, and neither pretends to: a lead needs
 * a Turnstile token (./turnstile), and a booking needs the visitor's phone
 * proved by SMS — which is why booking is two tools.
 */

import {
  createBooking,
  fetchBookingAvailability,
  fetchBookingPublic,
  sendOtp,
  verifyOtp,
  type BookingService,
} from "../components/booking-widget/booking-api";
import {
  CORE_FIELD_KEYS,
  fieldsFromData,
  missingRequiredFromData,
  splitCoreValues,
  type Data,
  type FieldDef,
  type FieldValue,
} from "../forms/form-data";
import { submitLead } from "../forms/submit-lead";
import { isPreviewMode } from "../lib/interaction-tracker";
import { getWindowJobsiteConfig, type JobsiteRuntimeConfig } from "../types/runtime-config";
import { toolError, toolResult, type WebMcpTool } from "./model-context";
import {
  formInputSchema,
  formToolFields,
  labelsForIds,
  valuesFromInput,
  type FormToolField,
} from "./form-schema";
import { requestTurnstileToken } from "./turnstile";

/** The documents behind the forms this site renders, resolved before
 *  registration. Null → the surface falls back to its preset, so the tool does. */
export interface JobsiteForms {
  contact?: Data | null;
  booking?: Data | null;
}

/** Arrival windows the booking API accepts in request mode. Mirrors the widget. */
const TIME_WINDOWS = ["asap", "morning", "afternoon", "evening"] as const;

/** Enough times to choose from without a wall of them to reason through. */
const MAX_SLOTS_PER_DAY = 12;

function str(input: Record<string, unknown>, key: string): string | undefined {
  const value = input[key];
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
}

/** The pageview tracker's session, so an agent lead attributes like any other.
 *  Read, never minted: an id no pageview used is an orphan. */
function sessionId(): string | null {
  if (isPreviewMode()) return null;
  try {
    return sessionStorage.getItem("sp_session_id");
  } catch {
    return null;
  }
}

/** "Dana Whitfield" → first/last, because the booking API stores them apart. */
function splitName(full: string | undefined): { firstName?: string; lastName?: string } {
  if (!full) return {};
  const parts = full.split(/\s+/);
  return { firstName: parts[0], lastName: parts.length > 1 ? parts.slice(1).join(" ") : undefined };
}

/** What an agent should offer instead when a tool can't finish the job. */
function fallback(config: JobsiteRuntimeConfig): Record<string, unknown> {
  const business = config.business ?? {};
  return {
    callInstead: business.phone ?? null,
    bookingUrl: business.bookingUrl ?? null,
    contactPage: typeof window !== "undefined" ? new URL("/contact", window.location.origin).href : null,
  };
}

/** `undefined` → the site has no widget, so the server won't ask for a token.
 *  `null` → a challenge went unsolved; callers report that rather than spend a
 *  request the server will reject. */
async function botCheck(config: JobsiteRuntimeConfig): Promise<string | null | undefined> {
  if (!config.turnstileSiteKey) return undefined;
  return requestTurnstileToken(config.turnstileSiteKey);
}

const BOT_CHECK_FAILED = "The site's bot check went unanswered, so nothing was submitted.";
const NO_ONLINE_BOOKING = "This site doesn't take online bookings.";

/** Services from the booking API, which is the only list carrying ids and prices. */
async function loadBookingServices(
  config: JobsiteRuntimeConfig,
): Promise<
  | { ok: true; apiUrl: string; slug: string; services: BookingService[] }
  | { ok: false; reason: string }
> {
  const { apiUrl } = config;
  const slug = config.business?.slug;
  if (!apiUrl || !slug) return { ok: false, reason: NO_ONLINE_BOOKING };
  const res = await fetchBookingPublic(apiUrl, slug);
  if (!res.ok) return { ok: false, reason: NO_ONLINE_BOOKING };
  return { ok: true, apiUrl, slug, services: res.data.services };
}

/** Match on id, slug, or name — an agent has whatever `list_services` gave it. */
function findService(services: BookingService[], query: string | undefined): BookingService | undefined {
  if (!query) return services.length === 1 ? services[0] : undefined;
  const needle = query.trim().toLowerCase();
  return (
    services.find((s) => s.id === query || s.slug === needle) ??
    services.find((s) => s.name.toLowerCase() === needle) ??
    services.find((s) => s.name.toLowerCase().includes(needle))
  );
}

function businessInfoTool(): WebMcpTool {
  return {
    name: "get_business_info",
    description:
      "Facts about this business: what it does, where it works, how to reach it, its Google rating, " +
      "and whether it takes emergency work or online bookings. Call this before answering anything " +
      "about the business rather than reading it off the page.",
    inputSchema: { type: "object", properties: {} },
    annotations: { readOnlyHint: true },
    execute: async () => {
      const config = getWindowJobsiteConfig();
      const business = config.business;
      if (!business?.name) return toolError("This page hasn't finished loading its business details.");

      return toolResult({
        name: business.name,
        phone: business.phone ?? null,
        emergencyPhone: business.emergencyPhone ?? null,
        email: business.email ?? null,
        address: business.formattedAddress ?? null,
        city: business.city ?? null,
        state: business.state ?? null,
        website: business.website ?? business.url ?? null,
        services: (business.services ?? [])
          .map((service) => (typeof service === "string" ? service : service.name))
          .filter(Boolean),
        serviceAreas: (business.serviceAreas ?? [])
          .map((area) => area.displayName ?? area.name)
          .filter(Boolean),
        open24Hours: business.is24Hours ?? false,
        // Null means the owner has never said either way — don't turn that into "no".
        emergencyService: business.emergencyService ?? null,
        googleRating: business.googleRating ?? null,
        googleReviewCount: business.googleReviewCount ?? null,
        bookingEnabled: business.bookingEnabled ?? false,
        bookingUrl: business.bookingUrl ?? null,
      });
    },
  };
}

function listServicesTool(): WebMcpTool {
  return {
    name: "list_services",
    description:
      "The services this business offers. Bookable services come back with an id, price and " +
      "duration; pass the id or name to check_availability or book_appointment.",
    inputSchema: { type: "object", properties: {} },
    annotations: { readOnlyHint: true },
    execute: async () => {
      const config = getWindowJobsiteConfig();
      const booking = await loadBookingServices(config);

      if (!booking.ok || !booking.services.length) {
        // Not bookable, but the business still lists what it does.
        const listed = (config.business?.services ?? [])
          .map((service) => (typeof service === "string" ? service : service.name))
          .filter(Boolean);
        return toolResult({
          bookable: false,
          services: listed.map((name) => ({ name })),
          howToBook: config.business?.phone
            ? `Call ${config.business.phone}, or use request_quote to have the business get in touch.`
            : "Use request_quote to have the business get in touch.",
        });
      }

      return toolResult({
        bookable: true,
        services: booking.services.map((service) => ({
          id: service.id,
          name: service.name,
          description: service.blurb || undefined,
          durationMinutes: service.duration,
          price: service.price,
          priceQualifier: service.priceQualifier,
          deposit: service.deposit,
          urgent: service.urgent,
          // "instant" takes an exact time; "request" sends a preferred day and
          // window the business confirms.
          scheduling: service.schedulingMode,
        })),
      });
    },
  };
}

function checkAvailabilityTool(): WebMcpTool {
  return {
    name: "check_availability",
    description:
      "When this business can take a job. For a service scheduled instantly this returns exact open " +
      "start times to pass to book_appointment; for one scheduled by request it returns the arrival " +
      "windows the business confirms afterwards.",
    inputSchema: {
      type: "object",
      properties: {
        service: { type: "string", description: "Service id or name from list_services." },
        days: { type: "number", description: "How many days ahead to look. 1-30, default 14." },
      },
    },
    annotations: { readOnlyHint: true },
    execute: async (input) => {
      const config = getWindowJobsiteConfig();
      const booking = await loadBookingServices(config);
      if (!booking.ok) return toolError(booking.reason, fallback(config));

      const service = findService(booking.services, str(input, "service"));
      if (!service) {
        return toolError("Name which service you mean.", {
          services: booking.services.map((s) => ({ id: s.id, name: s.name })),
        });
      }

      if (service.schedulingMode === "request") {
        return toolResult({
          service: { id: service.id, name: service.name },
          scheduling: "request",
          timeWindows: TIME_WINDOWS,
          note:
            "This service is booked by request: send a preferred date and window with " +
            "book_appointment and the business confirms the exact time.",
        });
      }

      const days = Math.min(Math.max(Number(input.days) || 14, 1), 30);
      const availability = await fetchBookingAvailability(booking.apiUrl, booking.slug, service.id, days);
      if (!availability.ok) return toolError(availability.error, fallback(config));

      return toolResult({
        service: { id: service.id, name: service.name },
        scheduling: "instant",
        timeZone: availability.timeZone,
        durationMinutes: service.duration,
        days: availability.days
          .filter((day) => day.isWorkingDay)
          .map((day) => ({
            date: day.date,
            // ISO instants — pass one back verbatim as `startsAt`.
            openTimes: day.slots
              .filter((slot) => slot.available)
              .slice(0, MAX_SLOTS_PER_DAY)
              .map((slot) => slot.startsAt),
          }))
          .filter((day) => day.openTimes.length > 0),
      });
    },
  };
}

/** The built-in ContactForm preset — what a site with no saved form renders. */
const PRESET_QUOTE_SCHEMA = {
  type: "object",
  properties: {
    name: { type: "string", description: "The visitor's full name." },
    phone: { type: "string", description: "Phone number the business should call back." },
    email: { type: "string", description: "Email address, if they'd rather be emailed." },
    message: { type: "string", description: "What they need done, in their own words." },
    service: { type: "string", description: "Which service this is about." },
    address: { type: "string", description: "Service address, when the job is at a property." },
    preferredDate: { type: "string", description: "Preferred date, as YYYY-MM-DD." },
    preferredTime: { type: "string", description: "Preferred time of day, in words." },
    urgency: {
      type: "string",
      enum: ["emergency", "urgent", "routine"],
      description: "How soon they need it.",
    },
  },
  required: ["name"],
} as const;

const PRESET_QUOTE_KEYS = Object.keys(PRESET_QUOTE_SCHEMA.properties);

function namespaceCustom(values: Record<string, FieldValue>): Record<string, unknown> {
  const { core, custom } = splitCoreValues(values);
  return { ...core, ...(Object.keys(custom).length ? { customFields: custom } : {}) };
}

function requestQuoteTool(contactForm: Data | null | undefined): WebMcpTool {
  // The document the visitor's form is built from is the one an agent fills.
  const fields: FieldDef[] = contactForm ? fieldsFromData(contactForm) : [];
  const entries: FormToolField[] = fields.length ? formToolFields(fields) : [];
  const describesForm = entries.length > 0;

  return {
    name: "request_quote",
    description:
      "Send this business a quote request or message on the visitor's behalf. It arrives in the " +
      "owner's inbox exactly as the site's contact form would, and they reply directly. The fields " +
      "here are the ones this business actually asks for. Only call this with details the visitor " +
      "has given you, and tell them it has been sent.",
    inputSchema: describesForm ? formInputSchema(entries) : PRESET_QUOTE_SCHEMA,
    annotations: { readOnlyHint: false },
    execute: async (input) => {
      const config = getWindowJobsiteConfig();
      if (!config.apiUrl || !config.siteId) {
        return toolError("This site can't take messages right now.", fallback(config));
      }

      const values = describesForm
        ? valuesFromInput(entries, input)
        : Object.fromEntries(PRESET_QUOTE_KEYS.map((k) => [k, str(input, k)]).filter(([, v]) => v !== undefined));

      if (describesForm) {
        const missing = missingRequiredFromData(contactForm, values);
        if (missing.length) {
          return toolError(
            `This business's form needs: ${labelsForIds(fields, missing).join(", ")}.`,
            fallback(config),
          );
        }
      } else if (!values.name) {
        return toolError("A name is required to send a request.");
      }

      if (!values.phone && !values.email) {
        return toolError("A phone number or an email address is required — otherwise nobody can reply.");
      }

      // A saved form's extras are namespaced; the preset's are top-level keys,
      // which is where ContactForm puts them and where the server reads them.
      const submitted = describesForm ? namespaceCustom(values) : values;

      const turnstileToken = await botCheck(config);
      if (turnstileToken === null) return toolError(BOT_CHECK_FAILED, fallback(config));

      const result = await submitLead({
        apiUrl: config.apiUrl,
        siteId: config.siteId,
        backupApiUrl: config.backupApiUrl,
        businessName: config.business?.name,
        payload: {
          ...submitted,
          formType: "contact",
          // So an agent-sent lead is legible in the inbox, and measurable.
          source: "webmcp",
          variant: config.variant,
          sessionId: sessionId(),
          ...(turnstileToken ? { turnstileToken } : {}),
        },
      });

      if (result.status === "failed") {
        return toolError(result.errorMessage ?? "The request couldn't be sent.", fallback(config));
      }

      const businessName = config.business?.name ?? "The business";
      return toolResult({
        sent: true,
        held: result.status === "backed-up",
        message:
          result.status === "backed-up"
            ? `${businessName} is having a temporary technical problem, so the request is held ` +
              `safely and reaches their inbox once it clears. Tell the visitor to call if urgent.`
            : `${businessName} has the request and will reply directly.`,
        ...(result.status === "backed-up" ? fallback(config) : {}),
      });
    },
  };
}

function sendBookingCodeTool(): WebMcpTool {
  return {
    name: "send_booking_code",
    description:
      "Step one of booking: text a 6-digit verification code to the visitor's mobile. Booking " +
      "requires a verified phone, so call this first, ask the visitor to read out the code they " +
      "receive, then call book_appointment with it.",
    inputSchema: {
      type: "object",
      properties: { phone: { type: "string", description: "US mobile number to text the code to." } },
      required: ["phone"],
    },
    annotations: { readOnlyHint: false },
    execute: async (input) => {
      const config = getWindowJobsiteConfig();
      const phone = str(input, "phone");
      const slug = config.business?.slug;
      if (!phone) return toolError("A mobile number is required.");
      if (!config.apiUrl || !slug) return toolError(NO_ONLINE_BOOKING, fallback(config));

      const turnstileToken = await botCheck(config);
      if (turnstileToken === null) return toolError(BOT_CHECK_FAILED, fallback(config));

      const res = await sendOtp(config.apiUrl, phone, { slug, turnstileToken: turnstileToken ?? undefined });
      if (!res.ok) return toolError(res.error, { code: res.code, ...fallback(config) });

      return toolResult({
        sent: true,
        expiresInSec: 600,
        next: "Ask the visitor for the code, then call book_appointment with it.",
      });
    },
  };
}

/** Property names book_appointment defines itself — off limits to form fields. */
const BOOKING_FIXED_KEYS = [
  "phone", "code", "service", "startsAt", "preferredDate", "timeWindow",
  "name", "email", "notes", "address", "consent",
];

/** Core keys the verified-customer path fills, so the form can't demand them. */
const VERIFIED_CUSTOMER_KEYS = ["name", "email", "phone"];

function bookAppointmentTool(bookingForm: Data | null | undefined): WebMcpTool {
  const allFields: FieldDef[] = bookingForm ? fieldsFromData(bookingForm) : [];
  // Booking takes name/email/phone from the verified customer; the form's own
  // address and message ride the tool's `address` and `notes`. What's left is
  // the form's extra fields, which become schema properties of their own.
  const extraFields = allFields.filter((f) => !CORE_FIELD_KEYS.has(f.id));
  const entries: FormToolField[] = formToolFields(extraFields, BOOKING_FIXED_KEYS);

  return {
    name: "book_appointment",
    description:
      "Step two of booking: confirm the appointment using the code texted by send_booking_code. " +
      "Pass `startsAt` for a service scheduled instantly, or `preferredDate` and `timeWindow` for " +
      "one scheduled by request. The visitor must agree to be contacted about the job before you " +
      "set `consent`.",
    inputSchema: formInputSchema(entries, {
        phone: { type: "string", description: "The number send_booking_code texted." },
        code: { type: "string", description: "The 6-digit code the visitor received." },
        service: { type: "string", description: "Service id or name from list_services." },
        startsAt: {
          type: "string",
          description: "Exact start time from check_availability, for instantly scheduled services.",
        },
        preferredDate: { type: "string", description: "Preferred date, as YYYY-MM-DD." },
        timeWindow: {
          type: "string",
          enum: [...TIME_WINDOWS],
          description: "Preferred arrival window. Defaults to asap.",
        },
        name: { type: "string", description: "The visitor's full name." },
        email: { type: "string", description: "The visitor's email, for the confirmation." },
        notes: {
          type: "string",
          description:
            "Anything the business should know about the job. Only kept for services the business " +
            "schedules by request — an exact-time booking has nowhere to store it.",
        },
        address: { type: "string", description: "Where the job is, when it's at a property." },
        consent: {
          type: "boolean",
          description: "True only once the visitor has agreed to be contacted about this job.",
        },
      },
      ["phone", "code", "consent"],
    ),
    annotations: { readOnlyHint: false },
    execute: async (input) => {
      const config = getWindowJobsiteConfig();
      const phone = str(input, "phone");
      const code = str(input, "code");
      const slug = config.business?.slug;

      if (!phone || !code) return toolError("The phone number and the texted code are both required.");
      if (input.consent !== true) {
        return toolError("Ask the visitor to agree to be contacted about the job, then set consent.");
      }
      if (!config.apiUrl || !slug) return toolError(NO_ONLINE_BOOKING, fallback(config));

      const booking = await loadBookingServices(config);
      if (!booking.ok) return toolError(booking.reason, fallback(config));

      const service = findService(booking.services, str(input, "service"));
      if (!service) {
        return toolError("Name which service to book.", {
          services: booking.services.map((s) => ({ id: s.id, name: s.name })),
        });
      }

      const customFields = valuesFromInput(entries, input);
      // The form's own address/message fields are satisfied by the tool's
      // `address`/`notes`, so a required one of either is really enforced.
      const missing = missingRequiredFromData(bookingForm, {
        ...customFields,
        address: str(input, "address"),
        message: str(input, "notes"),
      }).filter((id) => !VERIFIED_CUSTOMER_KEYS.includes(id));
      if (missing.length) {
        return toolError(`This business's booking form needs: ${labelsForIds(allFields, missing).join(", ")}.`);
      }

      const startsAt = str(input, "startsAt");
      const instant = service.schedulingMode === "instant";
      // The server stamps an instant booking accepted off the service's mode,
      // with or without a time, so a missing `startsAt` puts a timeless job on
      // the owner's calendar. Refused before the code is spent.
      if (instant && !startsAt) {
        return toolError(
          `${service.name} is booked at an exact time. Call check_availability and pass one of its open times as startsAt.`,
          { service: { id: service.id, name: service.name } },
        );
      }

      const verified = await verifyOtp(config.apiUrl, phone, code);
      if (!verified.ok) return toolError(verified.error);

      const timeWindow = str(input, "timeWindow");
      const scheduling = instant && startsAt
        ? { slot: { startsAt } }
        : {
            request: {
              preferredDate: str(input, "preferredDate"),
              window:
                timeWindow && (TIME_WINDOWS as readonly string[]).includes(timeWindow)
                  ? timeWindow
                  : "asap",
              note: str(input, "notes"),
            },
          };

      // A known customer's own record beats a name from a chat transcript.
      const supplied = splitName(str(input, "name"));
      const firstName = verified.customer.firstName ?? supplied.firstName;
      const lastName = verified.customer.lastName ?? supplied.lastName;

      const result = await createBooking(config.apiUrl, {
        verificationId: verified.verificationId,
        slug,
        serviceId: service.id,
        firstName,
        lastName,
        email: verified.customer.email ?? str(input, "email"),
        consent: true,
        ...scheduling,
        address: str(input, "address"),
        ...(Object.keys(customFields).length ? { customFields } : {}),
        sessionId: sessionId(),
      });
      if (!result.ok) return toolError(result.error, fallback(config));

      const droppedNote = instant && !!str(input, "notes");
      return toolResult({
        booked: true,
        confirmationCode: result.booking.confCode,
        service: result.booking.serviceName,
        status: result.booking.status,
        message: `Booked with ${config.business?.name ?? "the business"}. Confirmation ${result.booking.confCode}.`,
        ...(droppedNote
          ? { noteNotAttached: "An exact-time booking stores no note — pass it on when you confirm, or call it through." }
          : {}),
      });
    },
  };
}

function listReviewsTool(): WebMcpTool {
  return {
    name: "list_reviews",
    description:
      "What this business's customers actually said, with the rating and how long ago. Use this " +
      "rather than reading review text off the page — the page shows only what the owner chose to " +
      "feature.",
    inputSchema: {
      type: "object",
      properties: {
        limit: { type: "number", description: "How many reviews to return. 1-20, default 5." },
        minRating: { type: "number", description: "Lowest star rating to include, 1-5. Default 1 (all)." },
      },
    },
    annotations: { readOnlyHint: true },
    execute: async (input) => {
      const config = getWindowJobsiteConfig();
      if (!config.apiUrl || !config.siteId) return toolError("This site has no reviews to show.");

      const limit = Math.min(Math.max(Number(input.limit) || 5, 1), 20);
      const minRating = Math.min(Math.max(Number(input.minRating) || 1, 1), 5);
      try {
        const res = await fetch(
          `${config.apiUrl}/api/sites/public/${config.siteId}/reviews?limit=${limit}&minRating=${minRating}`,
        );
        if (!res.ok) return toolError("Couldn't load reviews.", fallback(config));
        const json = (await res.json()) as {
          averageRating?: number;
          totalCount?: number;
          reviews?: {
            profileName?: string; rating?: number; reviewText?: string;
            timeAgo?: string; source?: string; ownerAnswer?: string | null;
          }[];
        };
        return toolResult({
          averageRating: json.averageRating ?? config.business?.googleRating ?? null,
          totalCount: json.totalCount ?? config.business?.googleReviewCount ?? null,
          reviews: (json.reviews ?? []).map((r) => ({
            author: r.profileName ?? null,
            rating: r.rating ?? null,
            text: r.reviewText || null,
            when: r.timeAgo ?? null,
            source: r.source ?? null,
            ownerReply: r.ownerAnswer || undefined,
          })),
        });
      } catch {
        return toolError("Couldn't load reviews.", fallback(config));
      }
    },
  };
}

const words = (value: string): string[] => value.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

const containsRun = (hay: string[], run: string[]): boolean =>
  run.length <= hay.length &&
  hay.some((_, i) => run.every((word, j) => hay[i + j] === word));

/** Whole words in either direction: "Fairview" is in "Fairview Heights", but
 *  "Hampton" is not in "Northampton" — a substring test says it is. */
function areaMatches(areaName: string, query: string): boolean {
  const area = words(areaName);
  const asked = words(query);
  if (!area.length || !asked.length) return false;
  return containsRun(area, asked) || containsRun(asked, area);
}

function checkServiceAreaTool(): WebMcpTool {
  return {
    name: "check_service_area",
    description:
      "Whether this business works in a given town, suburb or city. Answer coverage questions with " +
      "this rather than guessing from the areas named on the page.",
    inputSchema: {
      type: "object",
      properties: {
        location: { type: "string", description: "Town, suburb, city or neighbourhood to check." },
      },
      required: ["location"],
    },
    annotations: { readOnlyHint: true },
    execute: async (input) => {
      const config = getWindowJobsiteConfig();
      const location = str(input, "location");
      if (!location) return toolError("Name the town or suburb to check.");

      const areas = config.business?.serviceAreas ?? [];
      const named = areas
        .map((a) => ({ name: a.displayName || a.name || "", isPrimary: a.isPrimary }))
        .filter((a) => a.name);
      const match = named.find((a) => areaMatches(a.name, location));

      if (match) {
        return toolResult({
          status: "covered",
          matchedArea: match.name,
          isPrimaryArea: match.isPrimary ?? false,
          areasServed: named.map((a) => a.name),
        });
      }

      // "We also cover nearby" is a real answer, just not a definite one.
      if (areas.some((a) => a.surroundingAreas)) {
        return toolResult({
          status: "possibly_covered",
          note: `${config.business?.name ?? "This business"} lists nearby areas and says it also covers surrounding ones, but ${location} isn't named. Confirm by phone.`,
          areasServed: named.map((a) => a.name),
          callToConfirm: config.business?.phone ?? null,
        });
      }

      return toolResult({
        status: "not_listed",
        note: `${location} isn't among the areas this business lists.`,
        areasServed: named.map((a) => a.name),
        callToConfirm: config.business?.phone ?? null,
      });
    },
  };
}

/** Every tool a jobsite registers, in the order an agent would work through them. */
export function buildJobsiteWebMcpTools(forms: JobsiteForms = {}): WebMcpTool[] {
  return [
    businessInfoTool(),
    listServicesTool(),
    listReviewsTool(),
    checkServiceAreaTool(),
    checkAvailabilityTool(),
    requestQuoteTool(forms.contact),
    sendBookingCodeTool(),
    bookAppointmentTool(forms.booking),
  ];
}
